import { useEffect, useMemo } from 'react';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { DEFAULT_UNIT_ID_DISPLAY_NAME } from 'src/common/consts/platformConfigConsts';
import { createAsyncLoadOptions, getInitialOptions } from 'src/common/helpers/AsyncFilterHelpers';

import { getWebSdkTestsFilterDefinitions } from '../../../helpers/WebSdkConsts';
import { useWebSdkTestLabelsQuery } from '../../../hooks/useWebSdkTestLabelsQuery';
import { getWebSdkTestsFilterDefinitionsV2 } from '../components/filter-section-v2/getWebSdkTestsFilterDefinitionsV2';
import { useWebSDKFiltersStore } from '../components/filter-section-v2/store';

const INITIAL_OPTIONS_COUNT = 100;

interface UseWebSdkTestsFiltersParams<T extends { labels: OptionType[] }> {
	filterOptions: T;
	selectedLabels?: OptionType[];
	unitIdDisplayName?: string;
	isV2?: boolean;
}

interface UseWebSdkTestsFiltersReturn<T extends { labels: OptionType[] }> {
	filterDefinitions: IFilterDefinition[];
	mergedFilterOptions: T;
	isLoading: boolean;
}

export const useWebSdkTestsFilters = <T extends { labels: OptionType[] }>({
	filterOptions,
	selectedLabels = [],
	unitIdDisplayName = DEFAULT_UNIT_ID_DISPLAY_NAME,
	isV2 = false
}: UseWebSdkTestsFiltersParams<T>): UseWebSdkTestsFiltersReturn<T> => {
	// Get labels using React Query
	const { data: allLabels = [], isLoading } = useWebSdkTestLabelsQuery();

	const isSelectAllLabels = useWebSDKFiltersStore((state) => state.isSelectAllLabels);

	// Load labels into the store for "Select All" feature
	const { loadLabels } = useWebSDKFiltersStore();
	useEffect(() => {
		loadLabels();
	}, [loadLabels]);

	const initialOptions = useMemo(
		() => getInitialOptions(allLabels, selectedLabels, INITIAL_OPTIONS_COUNT),
		[allLabels, selectedLabels]
	);

	// Create async load function for searching labels
	const loadOptions = useMemo(
		() => createAsyncLoadOptions(allLabels, initialOptions),
		[allLabels, initialOptions]
	);

	// Get filter definitions with async loading for V2
	const filterDefinitions = useMemo(() => {
		if (isV2) {
			return getWebSdkTestsFilterDefinitionsV2(
				unitIdDisplayName,
				loadOptions,
				initialOptions,
				isSelectAllLabels
			);
		}
		return getWebSdkTestsFilterDefinitions();
	}, [isV2, unitIdDisplayName, loadOptions, initialOptions, isSelectAllLabels]);

	const mergedFilterOptions = useMemo(
		() => ({
			...filterOptions,
			labels: initialOptions
		}),
		[filterOptions, initialOptions]
	);

	return {
		filterDefinitions,
		mergedFilterOptions,
		isLoading
	};
};
